import { useStyles } from "./PageHeader.styles";
import type { PageHeaderProps } from "./types";

/**
 * This is a custom MyEROAD component wrapper. PageHeader or a component that renders it is expected to be used as a direct
 * child of a Page component, and to use one or all of PageBreadcrumb,
 * PageTitle, PageSubtitle, PageDescription, PageInfoItem, PageInfoWrapper, and NavTabs as child
 * nodes - as children or named.
 *
 * Standard examples:
 *
 * Children:
 * ```<PageHeader hasBottomNav>
 *   <PageBreadcrumb />
 *   <PageTitle title="Page Title" />
 *   <PageSubtitle />
 *   <PageDescription text="description" />
 *   <PageInfoWrapper>
 *     <PageInfoItem />
 *     <PageInfoItem />
 *   </PageInfoWrapper>
 *   <NavTabs />
 * </PageHeader>```
 *
 * Or named:
 * ```<PageHeader
 *   breadcrumb={<PageBreadcrumb />}
 *   title={<PageTitle title="Page Title Here" />}
 *   subtitle={<PageSubtitle />}
 *   description={<PageDescription text="description" />}
 *   info={
 *     <PageInfoWrapper>
 *       <PageInfoItem />
 *       <PageInfoItem />
 *     </PageInfoWrapper>
 *   }
 * />```
 *
 */
const PageHeader = ({
  children,
  breadcrumb,
  title,
  subtitle,
  description,
  info,
  hasTopNav,
  hasBottomNav,
  isBackCompat,
}: PageHeaderProps) => {
  const classes = useStyles({ hasBottomNav, hasTopNav });

  if (children) {
    return <header className={classes.header}>{children}</header>;
  }

  return (
    <header className={classes.header}>
      {(isBackCompat && (
        <div className={classes.backCompat}>
          {breadcrumb}
          {title}
        </div>
      )) || (
        <>
          {breadcrumb}
          {title}
        </>
      )}
      {subtitle}
      {description}
      {info}
    </header>
  );
};

export { PageHeader };
